import React from 'react';
import { Routes, Route } from 'react-router-dom';
import {
  Dashboard,
  ArcheIconManager,
  ArcheManagement,
  BetaKeyManager,
  MaintenanceControl,
  AdminReviewQueue,
} from './dev-console';
import { RolesPage, RoleDetailPage } from './dev-console/pages/roles';
import { PaymentsDashboard } from './dev-console/pages/payments';
import { CreditActivityPage } from './dev-console/pages/credit-activity';
import { AnalyticsDashboard } from './dev-console/pages/analytics';
import {
  ProvidersPage,
  ProviderSpendPage,
  ProviderHealthPage,
  ProviderDetailPage,
} from './dev-console/pages/providers';
import {
  HomePage,
  BrowsePage,
  ArcheDetailPage,
  EntityDetailPage,
  CategoryPage,
  RunArchePage,
} from './marketplace';
import {
  ContributorDashboard,
  PluginSubmissions,
  PluginSubmissionDetail,
  ArcheDetailPage as ContributorArcheDetailPage,
  WorkspaceAssetLibraryPage,
} from './contributor';
import { CreditsPage, CreditExhaustionListener } from './credits';
import { ThemeProvider } from '@/components/theme-provider';
import { ChromeProvider } from '@/arche/context/ChromeContext';
import { Layout } from '@/components/Layout';
import { Toaster } from '@/components/ui/toaster';
import { ScrollToTop } from '@/components/ScrollToTop';
import { RouteTracker } from '@/components/RouteTracker';
import { CookieBanner } from '@/components/CookieBanner';
import { GlobalFiasAIProvider } from '@/components/fias-ai/hooks/GlobalFiasAIContext';
import { GlobalFiasAIWindow } from '@/components/fias-ai/GlobalFiasAIWindow';
import {
  AuthProvider,
  ProtectedRoute,
  LoginPage,
  SignupPage,
  ForgotPasswordPage,
  MfaRecoveryCodesPage,
  AccountSettingsPage,
  AccountHintGuard,
  MyDataPage,
  WorkspaceControlsPage,
  SwitchDevUserPage,
  OAuthConsentPage,
  SessionExpiredListener,
  useAuth,
} from './auth';
import {
  ArcheInteractionPage,
  ConditionalProtectedRoute,
  PluginDevPreviewPage,
  ChatPage,
  BookingPage,
  BookingCancelPage,
  PublicListPage,
  SigningPage,
} from './arche';
import { CliAuthPage } from './developer/CliAuthPage';
// Imported eagerly, not lazily: Home renders the marketing site a signed-out
// visitor sees at the domain root, and it is the page the prerendered
// snapshot paints. A lazy chunk would replace that snapshot with a spinner
// on boot — a visible flash on the most-trafficked page on the site.
import { Home } from '@/components/Home';
import { MaintenancePage } from './components/MaintenancePage';
import { useMaintenanceMode } from './hooks/useMaintenanceMode';
import { UnreadIndicator } from './hooks/useUnreadIndicator';
import { SharedConversationPage } from './shared';
// Imported directly (not via the arche barrel) so the public download page
// does not pull the sender-side arche bundle in for an anonymous visitor.
import { PublicDownloadPage as FiasTransferPublicDownloadPage } from './arche/arches/fias-transfer/pages/PublicDownloadPage';
import { TermsOfServicePage, PrivacyPolicyPage, AcceptableUsePolicyPage } from './legal';

function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const { isActive, message } = useMaintenanceMode();
  const { user } = useAuth();

  if (isActive && !user?.roles?.includes('admin')) {
    return <MaintenancePage message={message} />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="fias-theme" attribute="class">
      <AuthProvider>
        <ChromeProvider>
          <Toaster />
          <ScrollToTop />
          {/* Fires `page_view` analytics events on every navigation.
            Mounted inside <BrowserRouter /> (set in main.tsx) so it can
            use useLocation. Renders nothing. */}
          <RouteTracker />
          {/* Listens for `fias:credits-exhausted` events from the API/streaming
            interceptors and navigates the user to the credits store via
            react-router. Mounted inside the router context so it can use
            useNavigate. Renders nothing. */}
          <CreditExhaustionListener />
          {/* Listens for `fias:session-expired` events from the api.ts 401
            interceptor and the AuthContext proactive refresh timer.
            Shows a modal with a Sign In button that preserves the user's
            current location for return after re-authentication. */}
          <SessionExpiredListener />
          {/* Surfaces the app-wide unread total on the tab title + favicon, and
            flashes a transient OS/taskbar notification when it rises while the
            tab is hidden. Cross-browser; reuses the header's notification-summary
            query (no extra fetch). Renders nothing. */}
          <UnreadIndicator />
          <CookieBanner />
          <MaintenanceGate>
            {/* Global Fias AI: mounted ABOVE <Routes> so the conversation, the
                WebSocket subscription, and any in-flight agent turn survive
                navigation. Do NOT move inside Layout (per-route element). */}
            <GlobalFiasAIProvider>
              <GlobalFiasAIWindow />
              <Routes>
                {/* Auth routes (public, no header) */}
                <Route path="/auth/login" element={<LoginPage />} />
                <Route path="/auth/signup" element={<SignupPage />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                {/* One-time recovery codes shown right after MFA enrollment.
                    Public on purpose: enrollment now KEEPS the session, but this
                    must still not sit behind ProtectedRoute — an involuntary
                    session-expiry redirect would unmount it and destroy the
                    irrecoverable codes (see MfaRecoveryCodesPage). */}
                <Route path="/auth/mfa-recovery-codes" element={<MfaRecoveryCodesPage />} />

                {/* Legal pages (public, no auth required) */}
                <Route path="/legal/terms" element={<TermsOfServicePage />} />
                <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/legal/acceptable-use" element={<AcceptableUsePolicyPage />} />

                {/* Shared conversation (public, no auth required) */}
                <Route path="/shared/:shareId" element={<SharedConversationPage />} />

                {/* Domain root. Deliberately NOT wrapped in <Layout>: signed-out
                    visitors get the marketing site, which carries its own header
                    and footer, and signed-in visitors are redirected into the app
                    before anything renders. */}
                <Route path="/" element={<Home />} />
                <Route
                  path="/auth/settings"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        {/* Security emails deep-link here with an account hint;
                            the guard interstitials when it disagrees with the
                            signed-in session (work vs personal account in one
                            browser) instead of rendering the wrong settings. */}
                        <AccountHintGuard>
                          <AccountSettingsPage />
                        </AccountHintGuard>
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/auth/switch-user"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <SwitchDevUserPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-data"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <MyDataPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/workspaces"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <WorkspaceControlsPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                {/* OAuth consent screen — auth required, with layout */}
                <Route
                  path="/oauth/authorize"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <OAuthConsentPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                {/* CLI auth — creates API key and redirects to localhost callback */}
                <Route
                  path="/developer/cli-auth"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CliAuthPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                {/* Plugin dev preview — local-development-only.
                The page loads arbitrary plugin code into an iframe and
                hands it a real platform bridge + auth token. In any
                non-development build a phishing link of the shape
                /a/arche_plugins/dev-preview?url=https://attacker
                would otherwise drain credits / read storage as the
                victim. We omit the route from the build entirely
                outside `pnpm dev`; even an authenticated victim hitting
                the URL in production gets a 404 from the catch-all. */}
                {import.meta.env.DEV && (
                  <Route
                    path="/a/arche_plugins/dev-preview"
                    element={
                      <ProtectedRoute>
                        <PluginDevPreviewPage />
                      </ProtectedRoute>
                    }
                  />
                )}
                {/* Community chat lives at /chat */}
                <Route
                  path="/chat"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ChatPage defaultChannelId="cch_global_fias_community" />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                {/* Calendar booking pages — no auth, no layout */}
                <Route path="/a/arche_calendar/book/:slug" element={<BookingPage />} />
                <Route path="/a/arche_calendar/cancel/:slug" element={<BookingCancelPage />} />
                {/* Public todo list pages — no auth, no layout */}
                <Route path="/a/arche_todo_lists/public/:slug" element={<PublicListPage />} />
                <Route
                  path="/a/arche_todo_lists/public/:slug/completed"
                  element={<PublicListPage />}
                />
                {/* Document Sign signing page — no auth, no layout. Token in URL.
                Registered before /a/:id/* so the catch-all doesn't dispatch
                through ArcheInteractionPage and trigger an auth check. */}
                <Route path="/a/arche_document_sign/sign/:token" element={<SigningPage />} />
                {/* Fias Transfer public download page — no auth, no layout.
                The share token in the URL is the ONLY credential; the
                recipient has no Fias account. Registered before /a/:id/* for
                the same reason as the signing page above. */}
                <Route
                  path="/a/arche_fias_transfer/d/:token"
                  element={<FiasTransferPublicDownloadPage />}
                />
                {/* Arche interaction route (conditionally protected - public arches don't require auth) */}
                <Route
                  path="/a/:id/*"
                  element={
                    <ConditionalProtectedRoute>
                      <ArcheInteractionPage />
                    </ConditionalProtectedRoute>
                  }
                />
                {/* Marketplace routes (protected) */}
                <Route
                  path="/marketplace"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <HomePage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketplace/browse"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <BrowsePage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketplace/arches/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ArcheDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketplace/entities/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <EntityDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketplace/arches/:id/run"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <RunArchePage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketplace/categories/:slug"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CategoryPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                {/* Contributor routes (protected) */}
                <Route
                  path="/contributor/dashboard"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ContributorDashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contributor/arches/:archeId"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ContributorArcheDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contributor/workspaces"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <WorkspaceAssetLibraryPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                {/*
              Submission pages are for any authenticated contributor — they
              show *their own* submissions. The detail endpoint is the
              authorization boundary (a contributor can only fetch a submission
              they own; admin, support and plugin_approver can fetch any). The
              approve/reject actions inside the detail page are gated
              client-side on admin OR plugin_approver AND backend-gated.
            */}
                <Route
                  path="/submissions"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <PluginSubmissions />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/submissions/:submissionId"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <PluginSubmissionDetail />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                {/* Credits routes (protected) */}
                <Route
                  path="/credits"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CreditsPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                {/* Dev console routes (protected, admin only) */}
                <Route
                  path="/dev-console"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dev-console/arche-icons"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <Layout>
                        <ArcheIconManager />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dev-console/arche-management"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <Layout>
                        <ArcheManagement />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dev-console/roles"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <Layout>
                        <RolesPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dev-console/roles/:role"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <Layout>
                        <RoleDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dev-console/beta-keys"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <Layout>
                        <BetaKeyManager />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dev-console/payments"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <Layout>
                        <PaymentsDashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dev-console/credit-activity"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <Layout>
                        <CreditActivityPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dev-console/analytics"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <Layout>
                        <AnalyticsDashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dev-console/providers"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <Layout>
                        <ProvidersPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dev-console/providers/spend"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <Layout>
                        <ProviderSpendPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dev-console/providers/health"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <Layout>
                        <ProviderHealthPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dev-console/providers/:provider"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <Layout>
                        <ProviderDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dev-console/maintenance"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <Layout>
                        <MaintenanceControl />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                {/* Support shares this queue with admin — it is alerted when an
                    arche awaits human review — as does plugin_approver, whose
                    whole purpose is resolving these. Approve/reject inside the
                    page admits admin and plugin_approver but not support (see
                    routes/plugins/admin.ts). */}
                <Route
                  path="/dev-console/admin/reviews"
                  element={
                    <ProtectedRoute requiredRoles={['admin', 'support', 'plugin_approver']}>
                      <Layout>
                        <AdminReviewQueue />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </GlobalFiasAIProvider>
          </MaintenanceGate>
        </ChromeProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
